<template>
  <div class="add-client-wrapper">
    <!-- ===== PAGE HEADER ===== -->
    <div class="page-header">
      <div class="header-left">
        <h2>{{ form.user_id ? "Edit Client" : "Add New Client" }}</h2>
        <p class="text-muted">
          {{ form.user_id
            ? "Update client details and preferences."
            : "Enter client details and personalize preferences for automation, billing, and communication." }}
        </p>
      </div>
      <div class="header-right">
        <button class="btn-secondary" @click="window.history.back()">
          <i class="bx bx-x"></i> Discard
        </button>
        <button class="btn-primary" @click="saveClient">
          <i class="bx bx-save"></i> {{ form.user_id ? "Update Client" : "Save Client" }}
        </button>
      </div>
    </div>

    <!-- ===== BODY SECTION ===== -->
    <div class="client-body">
      <!-- LEFT: CLIENT FORM -->
      <div class="client-form">
        <div class="card fade-in">
          <h3 class="section-title">Client Information</h3>

          <div class="form-row">
            <div class="form-group half">
              <label>First Name</label>
              <input v-model="form.fname" type="text" placeholder="John" />
            </div>
            <div class="form-group half">
              <label>Last Name</label>
              <input v-model="form.lname" type="text" placeholder="Doe" />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group half">
              <label>Email</label>
              <input v-model="form.email" type="email" placeholder="client@example.com" />
            </div>
            <div class="form-group half">
              <label>Phone</label>
              <input v-model="form.mobile" type="text" placeholder="+1 234 567 890" />
            </div>
          </div>

          <!-- <div class="form-group">
            <label>Alternate Number (Optional)</label>
            <input type="text" placeholder="+1 987 654 321" />
          </div> -->

          <h3 class="section-title">Address Details</h3>

          <div class="form-row">
            <div class="form-group half">
              <label>Address Line 1</label>
              <input v-model="form.address" type="text" placeholder="123 Main Street" />
            </div>
            <div class="form-group half">
              <label>Address Line 2</label>
              <input type="text" placeholder="Suite 201" />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group third">
              <label>City</label>
              <input type="text" placeholder="San Francisco" />
            </div>
            <div class="form-group third">
              <label>State</label>
              <input type="text" placeholder="CA" />
            </div>
            <div class="form-group third">
              <label>Country</label>
              <input type="text" placeholder="USA" />
            </div>
          </div>

          <div class="form-group">
            <label>Zip Code</label>
            <input type="number" maxlength="6" placeholder="94105" />
          </div>
        </div>
      </div>

      <!-- RIGHT: SMART SETTINGS -->
      <div class="client-settings fade-in">
        <h3 class="section-title">Smart Client Preferences</h3>

        <div class="settings-grid">
          <!-- Reminder Card -->
          <div class="setting-card">
            <div class="card-header">
              <i class="bx bx-bell card-icon"></i>
              <div>
                <h4>Payment Reminders</h4>
                <p>Automatically notify clients before invoice due date.</p>
              </div>
            </div>
            <div class="card-body">
              <label class="switch">
                <input type="checkbox" />
                <span class="slider"></span>
              </label>
              <div class="reminder-inputs">
                <input type="number" min="1" placeholder="3" />
                <span>Days Before Due</span>
                <button class="btn-icon"><i class="bx bx-plus"></i></button>
              </div>
            </div>
          </div>

          <!-- Late Fees -->
          <div class="setting-card">
            <div class="card-header">
              <i class="bx bx-error card-icon"></i>
              <div>
                <h4>Late Fees</h4>
                <p>Add automatic late fees for overdue invoices.</p>
              </div>
            </div>
            <div class="card-body">
              <div class="form-row">
                <div class="form-group half">
                  <label>Fee (%)</label>
                  <input type="number" placeholder="5" min="0" />
                </div>
                <div class="form-group half">
                  <label>Fee Type</label>
                  <select>
                    <option>Invoice Value</option>
                    <option>Outstanding Balance</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <!-- Attachments -->
          <div class="setting-card">
            <div class="card-header">
              <i class="bx bx-paperclip card-icon"></i>
              <div>
                <h4>Invoice Attachments</h4>
                <p>Attach a PDF copy automatically in client emails.</p>
              </div>
            </div>
            <div class="card-body">
              <label class="switch">
                <input type="checkbox" />
                <span class="slider"></span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { ref, onMounted } from "vue";
import { addClient, getClientById } from "@/apis/clientapis";
import { useToast } from "vue-toastification";
const toast = useToast();

const loading = ref(false);
const form = ref({
  user_id: "",
  fname: "",
  lname: "",
  email: "",
  mobile: "",
  address: "",
  status: "1",
  user_cre_by: "",
});

onMounted(async () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const clientId = urlParams.get("id");
    const userData = JSON.parse(sessionStorage.getItem("ldata")) || {};

    form.value.user_cre_by = userData.user_id || "";

    if (clientId) {
      loading.value = true;
      const res = await getClientById(clientId);
      if (res?.status && res.client_dt) {
        const data = res.client_dt;

        form.value = {
          user_id: data.user_id || "",
          fname: data.fname || "",
          lname: data.lname || "",
          email: data.email || "",
          mobile: data.mobile || "",
          address: data.address_line1 || "",
          status: data.status || "1",
          user_cre_by: userData.user_id || "",
        };
      } else {
        toast.warning("Client not found!");
      }
    }
  } catch (err) {
    console.error("onMounted error:", err);
    toast.error("Error loading client details");
  } finally {
    loading.value = false;
  }
});

// 🔹 Submit client (create / update)
const saveClient = async () => {
  try {
    if (!form.value.fname) return toast.warning("Please enter first name");
    if (!form.value.email) return toast.warning("Please enter email");
    if (!form.value.mobile) return toast.warning("Please enter mobile number");

    loading.value = true;
    const res = await addClient(form.value);

    if (res?.status) {
      toast.success(res.success || "Client saved successfully");
      window.location.href = "/clients";
    } else {
      toast.error(res.success || "Failed to save client");
    }
  } catch (err) {
    console.error("saveClient error:", err);
    toast.error("Unexpected error while saving client");
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.add-client-wrapper {
  background: #fff;
  border-radius: 1rem;
  padding: 2.5rem;
  font-family: "Inter", sans-serif;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.05);
  animation: fadeIn 0.6s ease;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2.5rem;
  flex-wrap: wrap;
  gap: 1rem;
}
.page-header h2 {
  font-size: 1.7rem;
  font-weight: 700;
  color: #111827;
}
.text-muted {
  color: #6b7280;
  font-size: 0.9rem;
}
.header-right {
  display: flex;
  gap: 1rem;
}

.btn-primary,
.btn-secondary {
  border: none;
  border-radius: 8px;
  padding: 0.8rem 1.6rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}
.btn-primary {
  background: #111827;
  color: #fff;
}
.btn-primary:hover {
  background: #1f2937;
}
.btn-secondary {
  background: #f3f4f6;
  color: #111827;
}
.btn-secondary:hover {
  background: #e5e7eb;
}

.client-body {
  display: flex;
  gap: 2.5rem;
  align-items: flex-start;
}
.client-form {
  flex: 3;
}
.client-settings {
  flex: 1.6;
  position: sticky;
  top: 30px;
}

.card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  padding: 1.8rem;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.04);
}
.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1.4rem;
  color: #111827;
}

/* FORM STYLES */
.form-row {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}
.form-group {
  margin-bottom: 1.2rem;
}
label {
  display: block;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.4rem;
}
input,
select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: 0.3s ease;
}
input:focus,
select:focus {
  border-color: #111827;
  outline: none;
}
.form-group.half {
  flex: 1;
}
.form-group.third {
  flex: 1;
}

/* RIGHT SETTINGS PANEL */
.settings-grid {
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
}
.setting-card {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.2rem 1.4rem;
  transition: all 0.3s ease;
}
.setting-card:hover {
  transform: translateY(-3px);
  background: #f3f4f6;
}
.card-header {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  margin-bottom: 1rem;
}
.card-icon {
  background: #111827;
  color: #fff;
  padding: 0.5rem;
  border-radius: 8px;
  font-size: 1.2rem;
}
.card-header h4 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
}
.card-header p {
  color: #6b7280;
  font-size: 0.85rem;
  margin: 0.3rem 0 0 0;
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

/* SWITCH */
.switch {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 22px;
}
.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background-color: #d1d5db;
  border-radius: 34px;
  transition: 0.3s;
}
.slider:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  border-radius: 50%;
  transition: 0.3s;
}
input:checked + .slider {
  background-color: #111827;
}
input:checked + .slider:before {
  transform: translateX(18px);
}

.btn-icon {
  background: #f3f4f6;
  border: none;
  border-radius: 8px;
  padding: 0.3rem 0.5rem;
  cursor: pointer;
}
.btn-icon:hover {
  background: #e5e7eb;
}

.fade-in {
  animation: fadeIn 0.6s ease;
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 992px) {
  .client-body {
    flex-direction: column;
  }
  .client-settings {
    position: static;
    width: 100%;
  }
}

/* === 📦 ADD CLIENT PAGE PATCH === */

/* ---- Fade animations ---- */
.fade-in {
  animation: fadeIn 0.5s ease;
  opacity: 0;
  transform: translateY(8px);
  animation-fill-mode: forwards;
}
@keyframes fadeIn {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ---- Fix for sticky sidebar overlap ---- */
.client-settings {
  position: relative;
  z-index: 1;
}

/* ---- Buttons improvement ---- */
.btn-primary i,
.btn-secondary i {
  margin-right: 6px;
  font-size: 1rem;
  vertical-align: middle;
}

.btn-primary[disabled] {
  opacity: 0.7;
  cursor: not-allowed;
}

/* ---- Reminder Inputs Layout ---- */
.reminder-inputs {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.8rem;
}
.reminder-inputs input {
  width: 70px;
  text-align: center;
}
.reminder-inputs span {
  font-size: 0.85rem;
  color: #6b7280;
}
.btn-icon {
  background: #f3f4f6;
  border: none;
  border-radius: 8px;
  padding: 0.35rem 0.6rem;
  cursor: pointer;
  transition: 0.2s ease;
}
.btn-icon:hover {
  background: #e5e7eb;
}

/* ---- Form elements spacing ---- */
.form-group input,
.form-group select {
  font-size: 0.95rem;
  transition: border-color 0.25s ease;
}
.form-group input:focus,
.form-group select:focus {
  border-color: #111827;
  outline: none;
}

/* ---- Better spacing for card header icons ---- */
.setting-card .card-icon {
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ---- Better mobile spacing for card layout ---- */
@media (max-width: 992px) {
  .client-body {
    flex-direction: column !important;
    gap: 1.5rem !important;
  }
  .client-form,
  .client-settings {
    width: 100% !important;
  }
}

/* ---- Tablet Optimization ---- */
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.8rem;
  }
  .header-right {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    gap: 0.6rem;
  }
  .header-right button {
    flex: 1 1 48%;
  }
  .form-row {
    flex-direction: column;
  }
  .client-settings {
    position: static;
  }
  .setting-card {
    padding: 1rem;
  }
}

/* ---- Small screens ---- */
@media (max-width: 480px) {
  .add-client-wrapper {
    padding: 1.2rem;
    border-radius: 0.8rem;
  }
  .page-header h2 {
    font-size: 1.3rem;
  }
  .section-title {
    font-size: 1rem;
  }
  .setting-card .card-header h4 {
    font-size: 0.95rem;
  }
  .btn-primary,
  .btn-secondary {
    width: 100%;
    justify-content: center;
    font-size: 0.9rem;
  }
  .reminder-inputs {
    flex-direction: column;
    align-items: flex-start;
  }
  .reminder-inputs input {
    width: 100%;
  }
  .reminder-inputs span {
    font-size: 0.8rem;
  }
}

</style>
